// Video compression constants and validation
// Platform-specific implementations are in videoCompressionImpl.native.ts and videoCompressionImpl.web.ts

// Constants
export const MAX_VIDEO_DURATION_SECONDS = 15 * 60; // 15 minutes
export const MAX_VIDEO_FILE_SIZE_BYTES = 200 * 1024 * 1024; // 200MB
export const TARGET_MAX_RESOLUTION = 1080;

// Types (also exported from impl files to avoid circular imports)
export interface VideoCompressionResult {
  uri: string;
  size: number;
  duration: number;
  mimeType: string;
}

export interface CompressionProgress {
  stage: 'loading' | 'compressing' | 'finalizing';
  progress: number;
}

/**
 * Validate video file before processing
 */
export function validateVideoFile(
  fileSize: number,
  duration: number
): { valid: boolean; error?: string } {
  if (fileSize > MAX_VIDEO_FILE_SIZE_BYTES) {
    const maxSizeMB = Math.round(MAX_VIDEO_FILE_SIZE_BYTES / (1024 * 1024));
    return {
      valid: false,
      error: `Video file is too large. Maximum size is ${maxSizeMB}MB.`
    };
  }

  // Only check duration if we have a valid value (> 0)
  // On some platforms, duration may not be available
  if (duration > 0 && duration > MAX_VIDEO_DURATION_SECONDS) {
    const maxMinutes = MAX_VIDEO_DURATION_SECONDS / 60;
    return {
      valid: false,
      error: `Video is too long. Maximum duration is ${maxMinutes} minutes.`
    };
  }

  return { valid: true };
}

// Re-export platform-specific implementations
// Metro bundler will automatically resolve to .native.ts or .web.ts based on platform
export { compressVideo, getVideoMetadata } from './videoCompressionImpl';
